import {
  SignUpPayload,
  SignInPayload,
  SignResponse,
  CommonResponse,
  LogInResponse,
  TokenData,
  SocialLoginPayload
} from '../interfaces/auth';
import axiosInstance from '../axios';
import { AxiosError } from 'axios';
import { getCachedToken, readTokenFromStorage, setAuthToken, clearAuthToken } from '../tokenStore';

// Helper function to handle axios errors
const handleAxiosError = (error: AxiosError): never => {
  if (error.response?.data) {
    const errorData = error.response.data as any;
    throw new Error(errorData.response_message || 'An error occurred');
  }
  throw new Error(error.message || 'An error occurred');
};

// Login with email and password
export const login = async (payload: SignInPayload): Promise<LogInResponse> => {
  try {
    const response = await axiosInstance.post('/user/login/', payload);
    return response.data;
  } catch (error) {
    return handleAxiosError(error as AxiosError);
  }
};

// Register a new user
export const register = async (payload: SignUpPayload): Promise<SignResponse> => {
  try {
    const response = await axiosInstance.post('/user/register/', payload);
    return response.data;
  } catch (error) {
    return handleAxiosError(error as AxiosError);
  }
};

// Request password reset
export const forgotPassword = async (payload: { email: string }): Promise<CommonResponse> => {
  try {
    const response = await axiosInstance.post('/user/forgot-password/', payload);
    return response.data;
  } catch (error) {
    return handleAxiosError(error as AxiosError);
  }
};

// Reset password with token
export const resetPassword = async (
  uuid: string,
  token: string,
  payload: { password: string; confirm_password: string }
): Promise<CommonResponse> => {
  try {
    const response = await axiosInstance.post(`/user/reset-password/${uuid}/${token}/`, payload);
    return response.data;
  } catch (error) {
    return handleAxiosError(error as AxiosError);
  }
};

// Verify email
export const verifyEmail = async (uuid: string, token: string): Promise<CommonResponse> => {
  try {
    const response = await axiosInstance.get(`/user/verify-email/${uuid}/${token}/`);
    return response.data;
  } catch (error) {
    return handleAxiosError(error as AxiosError);
  }
};

// Resend verification email
export const resendEmailVerification = async (uuid: string, token: string): Promise<CommonResponse> => {
  try {
    const response = await axiosInstance.post(`/user/resend-verification/${uuid}/${token}/`, {});
    return response.data;
  } catch (error) {
    return handleAxiosError(error as AxiosError);
  }
};

// Social login
export const socialLogin = async (payload: SocialLoginPayload): Promise<LogInResponse> => {
  try {
    const response = await axiosInstance.post('/user/social-login/', payload);
    return response.data;
  } catch (error) {
    return handleAxiosError(error as AxiosError);
  }
};

// Store token in localStorage (and prime the in-memory cache)
export const storeToken = (tokenData: TokenData): void => {
  if (typeof window !== 'undefined') {
    localStorage.setItem('auth_token', JSON.stringify(tokenData));
    setAuthToken(tokenData.access || null);
  }
};

// Get the access token for API requests.
// Resolves from the in-memory cache / localStorage first, and only performs a
// getSession() network call once (cold start) instead of on every request.
export const getToken = async (): Promise<string | null> => {
  if (typeof window === 'undefined') return null;

  // 1) In-memory cache — kept fresh by <TokenSync />
  const cached = getCachedToken();
  if (cached) return cached;

  // 2) localStorage — synchronous, no network
  const stored = readTokenFromStorage();
  if (stored) {
    setAuthToken(stored);
    return stored;
  }

  // 3) Cold-start fallback — fetch the session once, then cache it
  try {
    const { getSession } = await import('next-auth/react');
    const session = await getSession();
    if (session?.accessToken) {
      setAuthToken(session.accessToken);
      return session.accessToken;
    }
  } catch (error) {
    console.error('Error getting token:', error);
  }
  return null;
};

// Get token data from localStorage
export const getTokenData = (): TokenData | null => {
  if (typeof window !== 'undefined') {
    const tokenData = localStorage.getItem('auth_token');
    if (tokenData) {
      try {
        return JSON.parse(tokenData);
      } catch (e) {
        return null;
      }
    }
  }
  return null;
};

// Logout - clear cached token and redirect home
export const logout = (): void => {
  clearAuthToken();
  if (typeof window !== 'undefined') {
    window.location.href = '/';
  }
};
